import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { MessageBubble } from '@/components/support/MessageBubble';
import { TypingIndicator } from '@/components/support/TypingIndicator';
import { useRealtimeMessages } from '@/hooks/useRealtimeMessages';
import { useTyping } from '@/hooks/useTyping';
import { usePresence } from '@/hooks/usePresence';
import { markMessagesRead } from '@/services/chatService';
import { cn } from '@/lib/utils';

export function LiveChatPanel({
  requestId,
  currentUid,
  peerUid,
  peerName,
  onSend,
  role,
  className,
  compact,
}: {
  requestId: string;
  currentUid: string;
  peerUid?: string | null;
  peerName?: string;
  onSend: (text: string) => Promise<void>;
  role: 'customer' | 'agent';
  className?: string;
  compact?: boolean;
}) {
  const { messages, loading } = useRealtimeMessages(requestId);
  const { othersTyping, notifyTyping } = useTyping(requestId, currentUid);
  const peerPresence = usePresence(peerUid);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, othersTyping.length]);

  useEffect(() => {
    void markMessagesRead(requestId, role);
  }, [requestId, role, messages.length]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    notifyTyping(false);
    try {
      await onSend(text);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className={cn('flex flex-col min-h-0 h-full', className)}>
      {peerUid && (
        <div className="shrink-0 px-3 py-2 border-b border-border flex items-center gap-2 text-xs text-muted-foreground">
          <span
            className={cn(
              'h-2 w-2 rounded-full shrink-0',
              peerPresence?.online ? 'bg-emerald-500' : 'bg-slate-400',
            )}
          />
          <span className="truncate">
            {peerName || 'Peer'}{' '}
            {peerPresence?.online
              ? peerPresence.status === 'idle'
                ? '· idle'
                : '· online'
              : '· offline'}
            {peerPresence?.pageUrl && role === 'agent' && ' · on page'}
          </span>
        </div>
      )}

      <div ref={scrollRef} className={cn('flex-1 overflow-y-auto space-y-2.5', compact ? 'px-3 py-3' : 'px-4 py-4')}>
        {loading && <div className="text-xs text-muted-foreground">Loading messages…</div>}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-8">No messages yet.</div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.messageId} message={m} isOwn={m.senderId === currentUid && m.senderRole !== 'system'} />
        ))}
        {othersTyping.length > 0 && <TypingIndicator label={`${peerName || 'Someone'} is typing`} />}
      </div>

      <div className="shrink-0 border-t border-border p-3">
        <div className="flex items-end gap-2 rounded-xl border border-border px-3 py-2 bg-slate-50 dark:bg-white/[0.03]">
          <textarea
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              notifyTyping(e.target.value.length > 0);
            }}
            onKeyDown={onKeyDown}
            rows={1}
            className="flex-1 resize-none bg-transparent text-[13px] leading-relaxed max-h-28 outline-none"
            placeholder="Type a message…"
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!input.trim() || sending}
            className="shrink-0 h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
